import React from 'react';

import { CloseOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import { RuleObject } from 'antd/es/form';
import styled from 'styled-components';

import Input from 'components/lib/Input';
import TextArea from 'components/lib/TextArea';

import { ACTION_PLAN_ITEM_ACTION_MAX_LENGTH } from './constants';
import CreateEditActionPlansModalDepartmentSelect from './CreateEditActionPlansModalDepartmentSelect';
import CreateEditActionPlansModalItemDatePicker from './CreateEditActionPlansModalItemDatePicker';
import CreateEditActionPlansModalOwnerSelect from './CreateEditActionPlansModalOwnerSelect';
import { OwnerOption, SelectOption } from './CreateEditActionPlansModalTypes';

const ActionItemsRow = styled.div`
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  column-gap: 10px;
  margin-bottom: 11px;
`;

const TimelineMarker = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  padding-top: 17px;

  &::before {
    content: '';
    position: absolute;
    top: -12px;
    bottom: -12px;
    left: 50%;
    border-left: 1px solid var(--line-gray);
  }
`;

const RowNumber = styled.div`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  display: grid;
  place-items: center;
  width: 20px;
  height: 19px;
  border: 1px solid var(--line-gray);
  border-radius: 50%;
  background: var(--white);
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1;
  padding-top: 4px;
`;

const ActionItemCard = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 310px 24px;
  gap: 10px;
  align-items: start;
  min-height: 88px;
  padding: 14px;
  border: 1px solid var(--line-gray);
  border-radius: 4px;
  background: var(--white);
`;

const RemoveRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
`;

const ControlsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(36px, auto) minmax(36px, auto);
  align-content: start;
  gap: 10px;
  min-height: 82px;
`;

const ControlRow = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 1fr) 130px;
  gap: 10px;
  align-items: start;
`;

const AddActionItemButton = styled.button`
  border: 1px solid var(--line-gray);
  background: var(--white);
  border-radius: 999px;
  padding: 7px 27px 7px 25px;
  cursor: pointer;
  font-size: 12px;
  color: var(--text-primary);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  margin-left: 38px;
  box-shadow: 0 2px 5px var(--box-shadow-gray-dark);
`;

const AddIcon = styled(PlusOutlined)`
  color: var(--active-blue);
`;

const ActionTextArea = styled(TextArea)`
  &&& {
    height: 87px;
    resize: none;
  }
`;

const FieldItem = styled(Form.Item)`
  && {
    margin-bottom: 0;
    width: 100%;

    .ant-form-item-control-input {
      min-height: 30px;
    }
  }
`;

const PlaceholderLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const OwnerIcon = styled(UserOutlined)`
  font-size: 14px;
  flex-shrink: 0;
  color: var(--text-primary);
`;

interface Props {
  itemTypeOptions: SelectOption[];
  ownerOptions: OwnerOption[];
}

const CreateEditActionPlansModalItemsList = ({ itemTypeOptions, ownerOptions }: Props) => {
  const form = Form.useFormInstance();

  const getItemRow = (index: number) => form.getFieldValue(['items', index]) ?? {};

  const hasRowValue = (row: any) =>
    Boolean(row?.id || row?.action?.trim?.() || row?.type_id || row?.owner_id || row?.due_date);

  const validateAction = (index: number) => async (_rule: RuleObject, value?: string) => {
    const row = getItemRow(index);
    if (hasRowValue(row) && !value?.trim()) {
      throw new Error('Action item is required');
    }

    if (value && value.length > ACTION_PLAN_ITEM_ACTION_MAX_LENGTH) {
      throw new Error('Action item must be 1024 characters or fewer');
    }
  };

  const validateType = (index: number) => async (_rule: RuleObject, value?: number) => {
    const row = getItemRow(index);
    if (hasRowValue(row) && typeof value !== 'number') {
      throw new Error('Department is required');
    }
  };

  return (
    <Form.List name="items">
      {(fields, { add, remove }) => (
        <>
          {fields.map((field) => (
            <ActionItemsRow key={field.key}>
              <Form.Item {...field} name={[field.name, 'id']} hidden>
                <Input />
              </Form.Item>

              <TimelineMarker>
                <RowNumber>{field.name + 1}</RowNumber>
              </TimelineMarker>
              <ActionItemCard>
                <FieldItem {...field} name={[field.name, 'action']} rules={[{ validator: validateAction(field.name) }]}>
                  <ActionTextArea maxLength={ACTION_PLAN_ITEM_ACTION_MAX_LENGTH} placeholder="Enter description" />
                </FieldItem>

                <ControlsGrid>
                  <FieldItem {...field} name={[field.name, 'owner_id']}>
                    <CreateEditActionPlansModalOwnerSelect
                      ownerOptions={ownerOptions}
                      placeholder={
                        <PlaceholderLabel>
                          <OwnerIcon />
                          Item Owner
                        </PlaceholderLabel>
                      }
                      suffixIcon={null}
                    />
                  </FieldItem>

                  <ControlRow>
                    <FieldItem
                      {...field}
                      name={[field.name, 'type_id']}
                      rules={[{ validator: validateType(field.name) }]}
                    >
                      <CreateEditActionPlansModalDepartmentSelect itemTypeOptions={itemTypeOptions} />
                    </FieldItem>

                    <FieldItem {...field} name={[field.name, 'due_date']}>
                      <CreateEditActionPlansModalItemDatePicker />
                    </FieldItem>
                  </ControlRow>
                </ControlsGrid>

                <Form.Item noStyle shouldUpdate>
                  {() =>
                    hasRowValue(getItemRow(field.name)) ? (
                      <RemoveRow type="button" onClick={() => remove(field.name)} aria-label="Remove action item">
                        <CloseOutlined />
                      </RemoveRow>
                    ) : (
                      <span />
                    )
                  }
                </Form.Item>
              </ActionItemCard>
            </ActionItemsRow>
          ))}

          <AddActionItemButton type="button" onClick={() => add({ action: '' })}>
            <AddIcon />
            Add Action Item
          </AddActionItemButton>
        </>
      )}
    </Form.List>
  );
};

export default CreateEditActionPlansModalItemsList;
